import React, { useState, useMemo } from "react";
import { Button } from "./ui/button";
import { useAppData } from "../hooks/useAppData";
import { useCuotasLote } from '../hooks/useCuotasLote';
import { useGrupoCuotas } from '../hooks/useGrupoCuotas';
import { CuotasPagosCard } from "./ui/CuotasPagosCard";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";

const CARDS = [
  {
    key: "clientes",
    title: "Clientes",
    desc: "Consulta tus clientes y sus cuotas",
    icon: (
      <svg width="28" height="28" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="7" r="4" /><path d="M5.5 21a7.5 7.5 0 0 1 13 0" /><circle cx="18" cy="8" r="2" /></svg>
    ),
  },
  {
    key: "comisiones",
    title: "Pago de Comisiones",
    desc: "Revisa el estado de tus comisiones",
    icon: (
      <svg width="28" height="28" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="20" height="20" rx="4" /><path d="M8 12h8m-8 4h6" /></svg>
    ),
  },
  {
    key: "venta",
    title: "Nueva Venta",
    desc: "Registra una nueva venta y sube documentos",
    icon: (
      <svg width="28" height="28" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="12" width="16" height="10" rx="2" /><path d="M3 12L14 3l11 9" /><path d="M14 17v-4" /><path d="M10 17h8" /></svg>
    ),
  }
];

// Datos mock para clientes, lotes y cuotas
const clientesMock = [
  {
    nombre: "Juan Pérez",
    lotes: [
      {
        numero: "101",
        cuotas: [
          { fecha: "2024-07-01", monto: 1000, pagada: true, vencida: false },
          { fecha: "2024-08-01", monto: 1000, pagada: false, vencida: false },
        ],
      },
      {
        numero: "102",
        cuotas: [
          { fecha: "2024-07-01", monto: 800, pagada: true, vencida: false },
          { fecha: "2024-08-01", monto: 800, pagada: false, vencida: true },
        ],
      },
    ],
  },
  {
    nombre: "Ana López",
    lotes: [
      {
        numero: "103",
        cuotas: [
          { fecha: "2024-07-01", monto: 1200, pagada: true, vencida: false },
          { fecha: "2024-08-01", monto: 1200, pagada: false, vencida: false },
        ],
      },
    ],
  },
];

// Datos mock para comisiones
const comisionesMock = [
  {
    comprador: "Juan Pérez",
    lotes: ["101", "102"],
    estado: "pagada",
  },
  {
    comprador: "Ana López",
    lotes: ["103"],
    estado: "pendiente",
  },
  {
    comprador: "Carlos Ruiz",
    lotes: ["104", "105"],
    estado: "pendiente",
  },
];

type Cuota = { fecha: string; monto: number; pagada: boolean; vencida: boolean };

function getCuotaEstado(cuota: Cuota, idx: number, cuotas: Cuota[]) {
  if (cuota.pagada) return 'pagada';
  if (cuota.vencida) return 'vencida';
  // próxima a pagar: la primera no pagada y no vencida
  const idxProxima = cuotas.findIndex((c: Cuota) => !c.pagada && !c.vencida);
  if (idx === idxProxima) return 'proxima';
  return 'futura';
}

function getCuotaColor(estado: string) {
  switch (estado) {
    case 'pagada': return 'bg-green-100 border-green-500 text-green-700';
    case 'proxima': return 'bg-orange-100 border-orange-500 text-orange-700';
    case 'futura': return 'bg-gray-100 border-gray-300 text-gray-500';
    case 'vencida': return 'bg-red-100 border-red-500 text-red-700';
    default: return 'bg-gray-100 border-gray-300 text-gray-500';
  }
}

function getComisionBadgeColor(estado: string) {
  if (estado === "pagada") return "bg-green-100 text-green-700 border-green-500";
  if (estado === "pendiente") return "bg-orange-100 text-orange-700 border-orange-500";
  return "bg-gray-100 text-gray-500 border-gray-300";
}

export default function VendedorDashboard({ user }: { user: { userType: string; userInput: string; userId?: string } }) {
  const { lots, comisiones, expenses } = useAppData();
  const [view, setView] = useState<string | null>(null);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<string | null>(null);
  const [loteSeleccionado, setLoteSeleccionado] = useState<string | null>(null);

  // Filtrar lotes vendidos a crédito por este vendedor (incluyendo lotes agrupados)
  const lotesVendedor = useMemo(() => {
    // Obtener todos los lotes vendidos por este vendedor
    const vendedorId = user.userId;
    const vendedorNombre = user.userInput;
    // Solo lotes a crédito
    const lotesCredito = lots.filter((lote: any) => lote.status === 'crédito' && lote.paymentType === 'pagos');
    // Filtrar por vendedor
    const lotesFiltrados = lotesCredito.filter((lote: any) => {
      if (lote.sellerId) {
        return lote.sellerId === vendedorId;
      }
      return (
        lote.seller === vendedorId ||
        lote.seller === vendedorNombre ||
        lote.vendedor === vendedorId ||
        lote.vendedor === vendedorNombre
      );
    });
    // Excluir individuales que estén agrupados
    const lotesIndividualesAExcluir = new Set<string>();
    lotesFiltrados.forEach((lote: any) => {
      if (Array.isArray(lote.idLotes) && lote.idLotes.length > 1) {
        lote.idLotes.forEach((id: string) => lotesIndividualesAExcluir.add(id));
      }
    });
    return lotesFiltrados.filter((lote: any) => {
      if (Array.isArray(lote.idLotes) && lote.idLotes.length > 1) return true;
      // Si es individual y no está en un grupo, mostrarlo
      return !lotesIndividualesAExcluir.has(lote.id || lote.numero);
    });
  }, [lots, user.userId, user.userInput]);

  // --- LOGS DE DEPURACIÓN ---
  console.log('user', user);
  console.log('lots', lots);
  console.log('lotesVendedor', lotesVendedor);
  console.log('lotesVendedor detalle', lotesVendedor.map((l: any) => ({
    id: l.id,
    numeroPagos: l.numeroPagos,
    numberOfPayments: l.numberOfPayments,
    paymentCount: l.paymentCount,
    totalValue: l.totalValue,
    valorTotal: l.valorTotal,
    saleDate: l.saleDate,
    fechaVenta: l.fechaVenta,
    payments: l.payments,
    status: l.status,
    sellerId: l.sellerId,
    seller: l.seller,
    vendedor: l.vendedor
  })));

  // Agrupar lotes por cliente
  const clientes = useMemo(() => {
    const map: { [nombre: string]: any[] } = {};
    lotesVendedor.forEach((lote: any) => {
      const nombre = lote.buyerName || lote.comprador || lote.buyerDPI || "Sin nombre";
      if (!map[nombre]) map[nombre] = [];
      map[nombre].push(lote);
    });
    return Object.entries(map).map(([nombre, lotes]) => ({ nombre, lotes }));
  }, [lotesVendedor]);

  // Comisiones del vendedor
  const comisionesVendedor = useMemo(() => {
    const vendedorId = user.userId;
    return comisiones.filter((c: any) => {
      return (
        c.sellerId === vendedorId
      );
    });
  }, [comisiones, user.userId]);

  // --- Estado de cuenta y cuotas para los lotes del vendedor ---
  // Agrupar por id de grupo (para lotes grupales)
  const lotesPorGrupo = useMemo(() => {
    const map: { [grupo: string]: any } = {};
    const lotesIndividualesAExcluir = new Set<string>();
    // Primero, identificar todos los lotes grupales y marcar los individuales a excluir
    lotesVendedor.forEach((lote: any) => {
      if (Array.isArray(lote.idLotes) && lote.idLotes.length > 1) {
        lote.idLotes.forEach((id: string) => lotesIndividualesAExcluir.add(id));
      }
    });
    lotesVendedor.forEach((lote: any) => {
      let grupoId = lote.id || lote.numero || '';
      let numero = lote.numero;
      // Si es grupal, construir el nombre concatenado
      if (Array.isArray(lote.idLotes) && lote.idLotes.length > 1) {
        const lotesOrdenados = [...lote.idLotes].sort((a, b) => parseInt(a) - parseInt(b));
        if (lotesOrdenados.length === 2) {
          numero = `${lotesOrdenados[0]}-${lotesOrdenados[1]}`;
        } else {
          numero = lotesOrdenados.join('-');
        }
        grupoId = numero;
        if (!map[grupoId]) map[grupoId] = { ...lote, numero };
      } else {
        // Si es individual y NO está en un grupo, mostrarlo
        if (!lotesIndividualesAExcluir.has(lote.id || lote.numero)) {
          if (!map[grupoId]) map[grupoId] = { ...lote, numero };
        }
      }
    });
    return Object.values(map);
  }, [lotesVendedor]);

  const grupoSeleccionado = loteSeleccionado
    ? lotesPorGrupo.find((l: any) => (l.id || l.numero) === loteSeleccionado)
    : null;
  const { historialPagos, cuotasPendientes, esCredito, esContado } = useGrupoCuotas(grupoSeleccionado, expenses);

  const { cuotasData, loading: loadingCuotas } = useCuotasLote(lotesPorGrupo);

  // --- LOG DE DEPURACIÓN DE CUOTAS ---
  console.log('cuotasData', cuotasData);

  // Flujo multinivel para Clientes
  let clientesView = null;
  if (view === "clientes") {
    if (loadingCuotas) {
      clientesView = (
        <div className="text-center text-gray-500 py-12">Cargando lotes a crédito...</div>
      );
    } else if (cuotasData.length === 0) {
      clientesView = (
        <div className="text-center text-gray-500 py-12 flex flex-col items-center gap-4">
          <div>
            No hay lotes a crédito registrados para este vendedor.<br />
            Si crees que esto es un error, contacta a soporte.
          </div>
        </div>
      );
    } else if (!loteSeleccionado) {
      clientesView = (
        <div className="w-full bg-white rounded-xl p-6 shadow-md">
          <div className="font-semibold text-lg mb-2">Lotes a Crédito Vendidos</div>
          <div className="space-y-3">
            {lotesPorGrupo.map((lote: any) => (
              <Card
                key={lote.id || lote.numero}
                className="bg-white border border-gray-200 border-l-4 border-l-blue-500 rounded-xl cursor-pointer hover:shadow-lg transition"
                onClick={() => setLoteSeleccionado(lote.id || lote.numero)}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-4 pr-4">
                  <CardTitle className="text-lg font-semibold text-gray-800">Ver Cuotas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">Lote {lote.id || lote.numero}</div>
                  <p className="text-xs text-gray-500 mt-1">Haz clic para ver detalles de pagos</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      );
    } else {
      // Mostrar detalle de cuotas/pagos con el componente compartido
      const loteData = cuotasData.find((l: any) => (l.numero === loteSeleccionado || l.numero === String(loteSeleccionado)));
      clientesView = loteData ? (
        <CuotasPagosCard loteData={loteData} onBack={() => setLoteSeleccionado(null)} />
      ) : null;
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center px-4 py-6">
      <div className="w-full max-w-md flex justify-between items-center mb-8">
        <h2 className="text-xl font-semibold text-left">Panel del Vendedor</h2>
        <div className="flex gap-2">
          {view && (
            <button
              className="px-3 py-1 rounded-md bg-gray-200 hover:bg-blue-500 hover:text-white text-gray-700 text-xs font-semibold transition-colors shadow-sm"
              onClick={() => setView(null)}
              title="Volver"
            >
              Volver
            </button>
          )}
          <button
            className="px-3 py-1 rounded-md bg-gray-200 hover:bg-red-500 hover:text-white text-gray-700 text-xs font-semibold transition-colors shadow-sm"
            onClick={() => {
              localStorage.removeItem('bimobiUser');
              window.location.reload();
            }}
            title="Cerrar sesión"
          >
            Cerrar sesión
          </button>
        </div>
      </div>
      <div className="w-full max-w-md space-y-4">
        {!view && CARDS.map(card => (
          <Card
            key={card.key}
            className="bg-white border border-gray-200 border-l-4 border-l-blue-500 rounded-xl cursor-pointer hover:shadow-lg transition"
            onClick={() => setView(card.key)}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-4 pr-4">
              <CardTitle className="text-lg font-semibold text-gray-800">Ingresar</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{card.title}</div>
              <p className="text-xs text-gray-500 mt-1">{card.desc}</p>
            </CardContent>
          </Card>
        ))}
        {/* Vistas internas */}
        {view === "clientes" && clientesView}
        {view === "comisiones" && (
          <div className="w-full bg-white rounded-xl p-6 shadow-md">
            <div className="font-semibold text-lg mb-4">Pago de Comisiones</div>
            <div className="space-y-3">
              {comisionesVendedor.map((com: any, idx: number) => (
                <Card key={idx} className={`bg-white border border-gray-200 border-l-4 ${(com.estado || (com.paid ? 'pagada' : 'pendiente')) === "pagada" ? 'border-l-green-500' : 'border-l-orange-500'
                  } rounded-xl shadow-sm`}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-4 pr-4">
                    <CardTitle className="text-lg font-semibold text-gray-800">{com.comprador || com.buyerName || com.buyerDPI}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">
                      Lote(s): {Array.isArray(com.lotes) && com.lotes.length > 0 ? com.lotes.join(", ") : (com.lotNumber || com.lotId || "-")}
                    </div>
                    <div className={`mt-2 px-3 py-1 rounded-full text-xs font-semibold inline-block ${(com.estado || (com.paid ? 'pagada' : 'pendiente')) === "pagada" ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                      {(com.estado || (com.paid ? 'pagada' : 'pendiente')) === "pagada" ? "Pagada" : "Pendiente"}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
        {view === "venta" && (
          <div className="w-full bg-white rounded-xl p-6 shadow-md">
            <div className="font-semibold text-lg mb-4">Nueva Venta</div>
            <NuevaVentaForm />
          </div>
        )}
      </div>
    </div>
  );
}

// --- NUEVO COMPONENTE ---
function NuevaVentaForm() {
  const [lotes, setLotes] = useState<string[]>([""]);
  const [boleta, setBoleta] = useState<File | null>(null);
  const [dpi, setDpi] = useState<File | null>(null);

  const boletaInputRef = React.useRef<HTMLInputElement>(null);
  const dpiInputRef = React.useRef<HTMLInputElement>(null);

  const handleLoteChange = (idx: number, value: string) => {
    setLotes(lotes.map((l, i) => (i === idx ? value : l)));
  };
  const handleAddLote = () => {
    setLotes([...lotes, ""]);
  };
  const handleRemoveLote = (idx: number) => {
    if (lotes.length > 1) setLotes(lotes.filter((_, i) => i !== idx));
  };
  const handleBoletaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) setBoleta(e.target.files[0]);
  };
  const handleDpiChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) setDpi(e.target.files[0]);
  };

  const isFormValid = lotes.every(l => l.trim() !== "") && boleta && dpi;

  return (
    <form className="space-y-6">
      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">Número de Lote(s)</label>
        <div className="space-y-2">
          {lotes.map((lote, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                type="text"
                required
                value={lote}
                onChange={e => handleLoteChange(idx, e.target.value)}
                placeholder={`Lote #${idx + 1}`}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
              />
              {lotes.length > 1 && (
                <button type="button" className="text-red-500 font-bold text-lg" onClick={() => handleRemoveLote(idx)} title="Quitar lote">×</button>
              )}
            </div>
          ))}
        </div>
        <button type="button" className="mt-2 text-blue-600 font-semibold underline" onClick={handleAddLote}>Añadir lote</button>
      </div>
      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">Boleta de Depósito</label>
        <input
          ref={boletaInputRef}
          type="file"
          accept="image/*,application/pdf"
          required
          onChange={handleBoletaChange}
          className="hidden"
        />
        <Button
          type="button"
          variant="info"
          className="w-full"
          onClick={() => boletaInputRef.current?.click()}
        >
          {boleta ? "Cambiar archivo" : "Seleccionar archivo"}
        </Button>
        {boleta && <div className="mt-1 text-xs text-blue-700 truncate">{boleta.name}</div>}
      </div>
      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">DPI del Comprador</label>
        <input
          ref={dpiInputRef}
          type="file"
          accept="image/*,application/pdf"
          required
          onChange={handleDpiChange}
          className="hidden"
        />
        <Button
          type="button"
          variant="info"
          className="w-full"
          onClick={() => dpiInputRef.current?.click()}
        >
          {dpi ? "Cambiar DPI" : "Seleccionar DPI"}
        </Button>
        {dpi && <div className="mt-1 text-xs text-blue-700 truncate">{dpi.name}</div>}
      </div>
      <button
        type="submit"
        className={`w-full py-3 rounded-lg font-semibold text-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300
          ${isFormValid ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
        disabled={!isFormValid}
      >
        Subir
      </button>
    </form>
  );
} 